import styles from './FinancialDataDisclaimer.module.css';
import {
  getPublicProviderLabel,
  isAttributionPublishable,
  DATA_PROVIDER,
} from '../../config/dataProvider';

/**
 * A small, reusable, provider-neutral notice about the market data: that it
 * comes from a third-party provider and is informational only (not advice).
 *
 * Provider language is sourced ENTIRELY from `src/config/dataProvider.js`, the
 * single source of truth for the data-provider identity and licensing state —
 * there are no hard-coded provider strings here. While licensing/attribution
 * is unconfirmed (the current, default state), this renders the neutral
 * "third-party market-data provider" wording and NO provider brand or link.
 * Once an FMP Data Display agreement is in place and `dataProvider.js` is
 * updated (see that file's header), the branded attribution line appears here
 * automatically — no change to this component required.
 *
 * Intentionally NOT placed contextually next to every chart/table yet: doing
 * so is only required once we carry a specific provider attribution, which is
 * the very thing gated off pending FMP permission. For now it lives on the
 * legal pages and (compactly) in the footer, which is sufficient for the
 * neutral informational notice.
 *
 * @param {object} props
 * @param {'block'|'inline'} [props.variant='block'] - `block` renders a full
 *   sentence for legal pages; `inline` renders a compact single line for the
 *   footer.
 */
const FinancialDataDisclaimer = ({ variant = 'block' }) => {
  const providerLabel = getPublicProviderLabel();
  const showBranded = isAttributionPublishable();

  return (
    <p className={variant === 'inline' ? styles.inline : styles.block}>
      {showBranded ? (
        // Only reachable once licensing is confirmed AND a required attribution
        // string has been supplied in dataProvider.js. Uses FMP's own required
        // wording/link verbatim from config — never a guessed string.
        DATA_PROVIDER.attribution.href ? (
          <a
            href={DATA_PROVIDER.attribution.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {DATA_PROVIDER.attribution.text}
          </a>
        ) : (
          <span>{DATA_PROVIDER.attribution.text}</span>
        )
      ) : (
        <>
          Market and financial data are provided by {providerLabel} for
          informational purposes only and do not constitute investment,
          financial, legal, or tax advice.
        </>
      )}
    </p>
  );
};

export default FinancialDataDisclaimer;
